/**
 * Payment API Logo ön kontrol hataları (HTTP 400, metaData = LogoPrerequisiteCheckResultDto).
 * @see rotary.services BackendNotes/LogoPrerequisiteErrors.md
 */

const PURPOSE_LABELS = {
  MEAL_DEBT_SLIP: 'Yemek borç fişi cari',
  DUES_DEBT_SLIP: 'Aidat borç fişi cari',
};

const ISSUE_CODES = {
  LOGO_CUSTOMER_NOT_FOUND: 'LOGO_CUSTOMER_NOT_FOUND',
  MEMBER_LOGO_CARI_MISSING: 'MEMBER_LOGO_CARI_MISSING',
  LOGO_ERP_UNREACHABLE: 'LOGO_ERP_UNREACHABLE',
  CONFIG_MISSING: 'CONFIG_MISSING',
};

function getErrorPayload(err) {
  return err?.details ?? err?.response?.data ?? {};
}

function getMeta(err) {
  const root = getErrorPayload(err);
  return root.metaData ?? root.MetaData ?? null;
}

function isNoiseMessage(msg) {
  if (!msg || typeof msg !== 'string') return true;
  const t = msg.trim();
  return t === '' || /^Request failed with status code \d+$/i.test(t);
}

/**
 * @param {object} issue
 * @returns {string}
 */
export function formatLogoPrerequisiteIssue(issue) {
  if (!issue) return '';
  if (issue.message) return issue.message;

  const checked = issue.logoCustomerCodeChecked ?? issue.LogoCustomerCodeChecked;
  const appCari = issue.memberApplicationCariCode ?? issue.MemberApplicationCariCode;
  const purpose = issue.logoCustomerPurpose ?? issue.LogoCustomerPurpose;
  const label = PURPOSE_LABELS[purpose] || 'Logo cari';
  const code = issue.code ?? issue.Code;

  if (code === ISSUE_CODES.LOGO_CUSTOMER_NOT_FOUND && checked) {
    if (appCari && appCari !== checked) {
      return `Logo'da ${label} '${checked}' bulunamadı (üye ana cari: '${appCari}').`;
    }
    return `Logo'da ${label} '${checked}' bulunamadı.`;
  }

  return code || 'Logo ön kontrol hatası';
}

/**
 * @param {Error} err
 * @returns {object[]}
 */
export function extractLogoPrerequisiteIssues(err) {
  const meta = getMeta(err);
  const issues = meta?.issues ?? meta?.Issues;
  return Array.isArray(issues) ? issues : [];
}

function uniqueCheckedCariCodes(issues) {
  const set = new Set();
  issues.forEach((issue) => {
    const c = issue.logoCustomerCodeChecked ?? issue.LogoCustomerCodeChecked;
    if (c) set.add(c);
  });
  return [...set];
}

/**
 * Toplu / tek işlem için başlık cümlesi (sebep + sonuç).
 * @param {object[]} issues
 * @param {object|null} meta
 * @param {{ bulk?: boolean, actionLabel?: string }} options
 */
function buildOperationHeadline(issues, meta, { bulk = false, actionLabel = 'İşlem' }) {
  const missingMembers = meta?.missingLogoCariMembers ?? meta?.MissingLogoCariMembers ?? [];
  const missingCariIssues = issues.filter(
    (i) => (i.code ?? i.Code) === ISSUE_CODES.MEMBER_LOGO_CARI_MISSING,
  );

  if (missingMembers.length > 0 || missingCariIssues.length > 0) {
    const n = Math.max(missingMembers.length, missingCariIssues.length, 1);
    if (bulk) {
      return `${n} üyenin uygulamada Logo cari kodu tanımlı olmadığından ${actionLabel} başlatılamadı.`;
    }
    return 'Üyede Logo cari kodu tanımlı olmadığından işlem yapılamadı.';
  }

  const notFound = issues.filter((i) => (i.code ?? i.Code) === ISSUE_CODES.LOGO_CUSTOMER_NOT_FOUND);
  if (notFound.length > 0) {
    const codes = uniqueCheckedCariCodes(notFound);
    if (codes.length === 1) {
      if (bulk) {
        return `Logo'da '${codes[0]}' cari kodu bulunamadığından ${actionLabel} başlatılamadı.`;
      }
      return `Logo'da '${codes[0]}' cari kodu bulunamadığından işlem yapılamadı.`;
    }
    if (codes.length > 1) {
      const list = codes.join(', ');
      if (bulk) {
        return `Logo'da şu cari kodlar bulunamadığından ${actionLabel} başlatılamadı: ${list}.`;
      }
      return `Logo'da şu cari kodlar bulunamadığından işlem yapılamadı: ${list}.`;
    }
    if (bulk) {
      return `Logo cari doğrulaması başarısız olduğu için ${actionLabel} başlatılamadı.`;
    }
    return 'Logo cari doğrulaması başarısız olduğu için işlem yapılamadı.';
  }

  const erpDown = issues.some((i) => (i.code ?? i.Code) === ISSUE_CODES.LOGO_ERP_UNREACHABLE);
  if (erpDown) {
    return bulk
      ? `Logo ERP'ye ulaşılamadığından ${actionLabel} başlatılamadı.`
      : "Logo ERP'ye ulaşılamadığından işlem yapılamadı.";
  }

  const configIssues = issues.filter(
    (i) => (i.code ?? i.Code) === ISSUE_CODES.CONFIG_MISSING,
  );
  if (configIssues.length > 0) {
    return bulk
      ? `Logo yapılandırması eksik olduğu için ${actionLabel} başlatılamadı.`
      : 'Logo yapılandırması eksik olduğu için işlem yapılamadı.';
  }

  return null;
}

/**
 * @param {Error} err
 * @param {{ bulk?: boolean, actionLabel?: string, preview?: boolean }} [options]
 * @returns {string}
 */
export function formatPaymentOperationError(err, options = {}) {
  const { bulk = false, preview = false, actionLabel = preview ? 'Önizleme' : bulk ? 'Toplu işlem' : 'İşlem' } =
    options;

  const root = getErrorPayload(err);
  const meta = getMeta(err);
  const issues = extractLogoPrerequisiteIssues(err);
  const summary = meta?.summaryMessage ?? meta?.SummaryMessage;

  if (issues.length > 0) {
    const headline = buildOperationHeadline(issues, meta, { bulk, actionLabel });
    const lines = issues.map(formatLogoPrerequisiteIssue).filter(Boolean);
    const uniqueLines = [...new Set(lines)];

    if (headline) {
      if (uniqueLines.length === 0) return headline;
      if (uniqueLines.length === 1 && uniqueLines[0] === headline) return headline;
      if (uniqueLines.length === 1) return `${headline}\n${uniqueLines[0]}`;
      return `${headline}\n${uniqueLines.join('\n')}`;
    }
    return uniqueLines.join('\n') || summary || root.message || `${actionLabel} tamamlanamadı.`;
  }

  if (summary && !isNoiseMessage(summary)) return summary;
  if (root.message && !isNoiseMessage(root.message)) return root.message;
  if (err?.message && !isNoiseMessage(err.message)) return err.message;

  if (bulk) return `${actionLabel} başlatılamadı (sunucudan ayrıntılı mesaj gelmedi).`;
  if (preview) return `${actionLabel} alınamadı (sunucudan ayrıntılı mesaj gelmedi).`;
  return `${actionLabel} tamamlanamadı (sunucudan ayrıntılı mesaj gelmedi).`;
}

/** @deprecated use formatPaymentOperationError */
export function formatLogoPrerequisiteError(err) {
  return formatPaymentOperationError(err, { bulk: false, actionLabel: 'İşlem' });
}
